"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findInjectionPoints = void 0;
const constants_1 = require("./constants");
const lib_1 = require("./lib");
function findInjectionPoints($, deps, element, eligibleForFinalSlot, slotInjectionLimits, options = {}, markup, injectionPointCallbackFunction) {
    const $e = $(element);
    if ($e.length === 0 || element === null) {
        return;
    }
    const { enabled } = deps;
    constants_1.viewPorts.forEach((breakpoint) => {
        lib_1.slotValues[breakpoint].preventInsertion = false;
    });
    // Never place ads between a header and content
    if ($e.is(':header') || (element.children.length === 1 && $(element.children[0]).is('strong'))) {
        constants_1.viewPorts.forEach((breakpoint) => {
            const scale = constants_1.CHARACTER_COUNT_SCALE[element.name] || constants_1.CHARACTER_COUNT_SCALE.default, len = Math.max(Math.round($e.text().trim().length * scale), constants_1.ESTIMATED_LINE_LENGTH[breakpoint]);
            (0, lib_1.incrementCharacterCount)(len, breakpoint);
            (0, lib_1._trace)(breakpoint, 'found header (length %d) (no slot insertion allowed immediately after)', len);
        });
        return;
    }
    if (!$e.length || !$e[0].next) {
        // console.trace('element', element);
        return;
    }
    const next = $e?.next();
    // Skip any previously injected slot containers
    if ($e.data('smart-slot') !== '' && $e.data('smart-slot') !== undefined && next.length > 0) {
        return;
    }
    if ((0, lib_1.isParagraph)($e)) {
        constants_1.viewPorts.forEach((breakpoint) => {
            lib_1.slotValues[breakpoint].sequentialEmbeds = 0;
            const len = (0, lib_1.characterCountForElement)($, $e, breakpoint, 'paragraph');
            let startsWith = '';
            try {
                startsWith = $e.text().slice(0, 20);
            }
            catch (error) {
                console.error(error);
            }
            (0, lib_1._trace)(breakpoint, `found paragraph (length %d); starts with: ${startsWith}`, len);
            if (len > 0) {
                lib_1.slotValues[breakpoint].paragraphs++;
                (0, lib_1.incrementCharacterCount)(len, breakpoint);
            }
        });
    }
    else if ((0, lib_1.isList)($e)) {
        constants_1.viewPorts.forEach((breakpoint) => {
            const len = (0, lib_1.characterCountForElement)($, $e, breakpoint, 'list');
            (0, lib_1._trace)(breakpoint, 'found list (length %d)', len);
            (0, lib_1.incrementCharacterCount)(Math.max(len, constants_1.ESTIMATED_LINE_LENGTH[breakpoint]), breakpoint);
            if ($e.find('li').length > 0) {
                // If we have even a single list item, we'll treat it as
                // breaking up a series of embeds.
                if (enabled)
                    lib_1.slotValues[breakpoint].sequentialEmbeds = 0;
            }
            // We'll account for each list as though it's a paragraph.
            lib_1.slotValues[breakpoint].paragraphs++;
        });
    }
    else if ((0, lib_1.isTable)($e)) {
        constants_1.viewPorts.forEach((breakpoint) => {
            // Count the number of table rows
            lib_1.slotValues[breakpoint].paragraphs += $e.find('table tr').length;
            const len = (0, lib_1.characterCountForElement)($, $e, breakpoint, 'table');
            (0, lib_1._trace)(breakpoint, 'found table (length %d)', len);
            (0, lib_1.incrementCharacterCount)(len, breakpoint);
        });
    }
    else if ((0, lib_1.isColumns)($e)) {
        constants_1.viewPorts.forEach((breakpoint) => {
            // Count the number of paragraphs
            lib_1.slotValues[breakpoint].paragraphs += $e.find('p').length;
            const len = (0, lib_1.characterCountForElement)($, $e, breakpoint);
            (0, lib_1._trace)(breakpoint, 'found columns (length %d)', len);
            (0, lib_1.incrementCharacterCount)(len, breakpoint);
        });
    }
    else if ((0, lib_1.isContentRecommendationSection)($e)) {
        constants_1.viewPorts.forEach((breakpoint) => {
            (0, lib_1._trace)(breakpoint, 'found content recommendation section (no slot insertion allowed immediately after)');
        });
        return;
    }
    else if ((0, lib_1.isGroupedPostsBlock)($e)) {
        constants_1.viewPorts.forEach((breakpoint) => {
            // related posts should not count toward character length, so
            // we can't trigger insertion based on an artifically inflated character
            // count.
            (0, lib_1._trace)(breakpoint, 'found grouped posts block (force insertion after, unless final element)');
            (0, lib_1.forceInsertion)(breakpoint);
        });
    }
    else if ((0, lib_1.isWordpressBlock)($e)) {
        constants_1.viewPorts.forEach((breakpoint) => {
            (0, lib_1._trace)(breakpoint, 'found wordpress block');
            const len = (0, lib_1.characterCountForElement)($, $e, breakpoint, 'wp-block');
            (0, lib_1.incrementCharacterCount)(len, breakpoint);
        });
    }
    else if ((0, lib_1.isAnchorSection)($e)) {
        constants_1.viewPorts.forEach((breakpoint) => {
            (0, lib_1._trace)(breakpoint, 'found anchor section (recursing)');
        });
        // Recursively descend into sections -- note that we want to do
        // this *before* checking for embeds.
        $e.children().each((_, child) => {
            findInjectionPoints($, deps, child, false, slotInjectionLimits, {}, markup, injectionPointCallbackFunction);
        });
    }
    else if ((0, lib_1.isEmbedCard)($e)) {
        constants_1.viewPorts.forEach((breakpoint) => {
            // TML Item Cards should have a minimum 200 characters of non-slot space
            // above and below (ADS-5616). We definitely don't want a slot just after
            // the card. Declare reserved space at this point, to prevent slot insertion
            // until we have reached the desired character spacing.
            // Add one character to the margin (200), since we want >200 characters
            // before we consider inserting a slot below the card.
            lib_1.slotValues[breakpoint].reservedSpace = constants_1.EMBED_CARD_MARGIN[breakpoint] + 1;
            (0, lib_1._trace)(breakpoint, 'found embed card (adding %d characters to reserved space) (no slot insertion allowed immediately after)', lib_1.slotValues[breakpoint].reservedSpace);
        });
        return;
    }
    else if ((0, lib_1.hasEmbed)($e)) {
        constants_1.viewPorts.forEach((breakpoint) => {
            (0, lib_1._trace)(breakpoint, 'found embed');
            lib_1.slotValues[breakpoint].embeds++;
            if (enabled) {
                lib_1.slotValues[breakpoint].sequentialEmbeds++;
                /**
                 * Avoid Breaking Up Images Too Aggressively
                 *
                 * If an image or an embed has a paragraph with fewer than 200 characters above or below,
                 * do not separate the image/embed and the paragraph.
                 *
                 * Do not insert ads between sequence of images or embeds unless there are four or more in
                 * sequence. When inserting ads into a sequence, do not leave a single leading or trailing
                 * image or embed. (Example: within a sequence 4 images the only valid injection point
                 * would be immediately after the first 2 images.)
                 *
                 * If the conditions for inserting a slot are met, and more than 6 images/embeds have been
                 * passed, insert a slot regardless of these rules. (To avoid the image / paragraph repeat
                 * pattern causing ads to never be inserted.)
                 *
                 */
                if (lib_1.slotValues[breakpoint].sequentialEmbeds < 6) {
                    // Don't orphan the first embed in a group of embeds
                    const prev = $e[0].prev && $($e[0].prev);
                    if (!(prev && (0, lib_1.hasEmbed)(prev)) && (0, lib_1.hasEmbed)(next)) {
                        (0, lib_1._trace)(breakpoint, '\tpreventing slot insertion after first embed in group');
                        lib_1.slotValues[breakpoint].preventInsertion = true;
                    }
                    // Don't orphan the last embed in a group of embeds
                    const nextNext = next[0].next && next.next();
                    if ((0, lib_1.hasEmbed)(next) && !(nextNext && (0, lib_1.hasEmbed)(nextNext))) {
                        (0, lib_1._trace)(breakpoint, '\tpreventing slot insertion immediately after embed group');
                        lib_1.slotValues[breakpoint].preventInsertion = true;
                    }
                }
            }
        });
    }
    else if (next.length > 0) {
        constants_1.viewPorts.forEach((breakpoint) => {
            (0, lib_1._trace)(breakpoint, 'unrecognized element near end of content (no slot insertion allowed immediately after)', $e);
            // Since the element isn't recognizable, let's skip it unless it's the last element
            if (enabled)
                lib_1.slotValues[breakpoint].sequentialEmbeds = 0;
        });
        return;
    }
    const prev = $e[0].prev && $($e[0].prev);
    if ((0, lib_1.isFloated)($e) || (prev && (0, lib_1.isFloated)(prev))) {
        constants_1.viewPorts.forEach((breakpoint) => {
            (0, lib_1._trace)(breakpoint, 'found floated element (no slot insertion allowed immediately after)');
        });
        // Don't inject a slot next to a floated element
        return;
    }
    if (enabled) {
        let slotHTML = '';
        constants_1.viewPorts.forEach((breakpoint) => {
            (0, lib_1._trace)(breakpoint, 'checking slot insertion spacing rules; slotValues %O', {
                ...lib_1.slotValues[breakpoint],
            });
            let insertSlot = false;
            if (lib_1.slotValues[breakpoint].preventInsertion) {
                (0, lib_1._trace)(breakpoint, '\tprevented insertion');
            }
            else if (lib_1.slotValues[breakpoint].reservedSpace !== 0) {
                (0, lib_1._trace)(breakpoint, '\treservedSpace is not 0');
            }
            else if (!(options.hasEmbedCards ?? false ? (0, lib_1.embedCardRule)($, $e, breakpoint) : true)) {
                (0, lib_1._trace)(breakpoint, '\tembedCardRule failed');
            }
            else {
                // Determine if e is the final element at this scope
                const isFinalElement = next.length === 0;
                const initialCheck = !isFinalElement &&
                    (!!lib_1.slotValues[breakpoint].forceInsertion ||
                        (0, lib_1.isValidInitialSlot)(breakpoint, lib_1.slotValues[breakpoint], slotInjectionLimits)) &&
                    lib_1.slotCounts[breakpoint] < constants_1.subsequentSlotThreshold;
                const subsequentCheck = !isFinalElement &&
                    (!!lib_1.slotValues[breakpoint].forceInsertion ||
                        (0, lib_1.isValidSubsequentSlot)(breakpoint, lib_1.slotValues[breakpoint], slotInjectionLimits)) &&
                    lib_1.slotCounts[breakpoint] >= constants_1.subsequentSlotThreshold;
                const finalCheck = eligibleForFinalSlot &&
                    isFinalElement &&
                    (!!lib_1.slotValues[breakpoint].forceInsertion ||
                        (0, lib_1.isValidFinalSlot)(breakpoint, lib_1.slotValues[breakpoint], lib_1.slotCounts[breakpoint], constants_1.subsequentSlotThreshold, slotInjectionLimits));
                if (initialCheck) {
                    (0, lib_1._trace)(breakpoint, '\tpasses initial insertion test');
                    insertSlot = true;
                }
                else if (subsequentCheck) {
                    (0, lib_1._trace)(breakpoint, '\tpasses subsequent insertion test');
                    insertSlot = true;
                }
                else if (finalCheck) {
                    (0, lib_1._trace)(breakpoint, '\tpasses final insertion test');
                    insertSlot = true;
                }
                else {
                    (0, lib_1._trace)(breakpoint, '\tfailed all 3 slot insertion tests');
                }
            }
            if (insertSlot) {
                (0, lib_1._trace)(breakpoint, '\t👉 Attemping slot insertion for breakpoint ' + breakpoint);
                const slot = injectionPointCallbackFunction(breakpoint, $e, markup);
                if (!slot) {
                    (0, lib_1._trace)(breakpoint, '\t🚨 Slot insertion failed due to smart-slot config ' + breakpoint);
                    return;
                }
                (0, lib_1._trace)(breakpoint, '\t✅ Slot insertion succeeded for breakpoint ' + breakpoint);
                slotHTML += slot;
                (0, lib_1.resetSlotValues)(breakpoint);
                lib_1.slotCounts[breakpoint]++;
            }
        });
        if (slotHTML !== '') {
            $e.after(slotHTML);
        }
    }
}
exports.findInjectionPoints = findInjectionPoints;
//# sourceMappingURL=slotContainers.js.map